import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  ReportMatrix,
  formatValue,
  matrixToCsv,
} from '../ReportsNative';
import { useColumnPrefs } from '../../pages/reports/useColumnPrefs';
import { GlDrillModal } from '../../pages/reports/GlDrillModal';
import { buildFinancialPnlArtifactMatrix } from '../../utils/financialPnlMatrix';
import './PnLReportTable.css';

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function periodLabel(data) {
  if (data?.title) return data.title;
  const p = data?.period;
  if (!p) return 'P&L';
  const sy = p.startYear || p.year;
  const ey = p.endYear || p.year || sy;
  const sm = p.startMonth || 1;
  const em = p.endMonth || 12;
  if (!sy) return 'P&L';
  if (sy === ey && sm === em) return `${sm}/${sy}`;
  return `${sm}/${sy}-${em}/${ey}`;
}

function periodDates(period, monthKey = null) {
  if (monthKey && monthKey !== 'total') {
    const [year, month] = String(monthKey).slice(0, 7).split('-').map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    return {
      startDate: `${year}-${String(month).padStart(2, '0')}-01`,
      endDate: `${year}-${String(month).padStart(2, '0')}-${lastDay}`,
      label: new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    };
  }

  const sy = period?.startYear || period?.year;
  const ey = period?.endYear || period?.year || sy;
  const sm = period?.startMonth || 1;
  const em = period?.endMonth || 12;
  if (!sy) return null;
  const lastDay = new Date(ey, em, 0).getDate();
  return {
    startDate: `${sy}-${String(sm).padStart(2, '0')}-01`,
    endDate: `${ey}-${String(em).padStart(2, '0')}-${lastDay}`,
    label: periodLabel({ period }),
  };
}

const PnLReportTable = ({ data, hotelId, orgId, currency = 'USD' }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [glDrill, setGlDrill] = useState(null);
  const parsedLocationId = hotelId && hotelId !== 'all' ? Number(hotelId) : null;
  const locationId = Number.isFinite(parsedLocationId) ? parsedLocationId : null;
  const organizationId = orgId || localStorage.getItem('currentOrgId');
  const openGlDrill = useCallback(
    (row, suffix, value) => {
      const d = row?.drill;
      const dates = periodDates(data?.period, suffix);
      if (!d || !organizationId || !dates) return;
      setGlDrill({
        accountLabel: d.accountLabel,
        periodLabel: dates.label,
        expected: value,
        params: {
          organizationId: Number(organizationId),
          locationIds: [Number(d.locationId)],
          startDate: dates.startDate,
          endDate: dates.endDate,
          filters: { accountIds: [Number(d.accountId)] },
          page: 1,
          pageSize: 500,
          sort: { field: 'date', dir: 'asc' },
        },
      });
    },
    [data?.period, organizationId]
  );
  const actualCellRender = useCallback(
    (suffix) => (value, row) => {
      const formatted = formatValue(value, 'number');
      if (!row?.drill || value === null || value === undefined) return formatted;
      return (
        <button
          type="button"
          className="rn-gl-drill"
          title="View the GL transactions behind this amount"
          onClick={(e) => {
            e.stopPropagation();
            openGlDrill(row, suffix, value);
          }}
        >
          {formatted}
        </button>
      );
    },
    [openGlDrill]
  );
  const matrix = useMemo(
    () => buildFinancialPnlArtifactMatrix(data, { showBudget: true, actualCellRender, defaultLocationId: locationId }),
    [data, actualCellRender, locationId]
  );
  const columnPrefs = useColumnPrefs('financialPnl', matrix.columns);
  const title = periodLabel(data);

  const handleDownloadCsv = () => {
    const csv = matrixToCsv({
      columns: matrix.columns,
      rows: matrix.rows,
      footer: matrix.footer,
      rowHeaderLabel: 'Location / Account',
    });
    downloadCsv(`assistant-financial-pnl.csv`, csv);
  };

  if (!data || typeof data !== 'object') {
    return (
      <div className="pnl-report-container pnl-report-container--native">
        <div className="pnl-footer-hint" style={{ padding: '12px 16px' }}>
          <span>P&L data unavailable for this period.</span>
        </div>
      </div>
    );
  }

  if (collapsed) {
    return (
      <section className="rn-card ai-pnl-collapsed">
        <button
          type="button"
          className="ai-pnl-collapsed-btn"
          onClick={() => setCollapsed(false)}
          aria-expanded="false"
        >
          <ChevronRight size={15} />
          <span>Profit & Loss</span>
          <small>{title}</small>
        </button>
      </section>
    );
  }

  return (
    <>
      <div className="pnl-report-container pnl-report-container--native">
        <ReportMatrix
          title="Income Statement"
          pill={title}
          rowHeaderLabel="Location / Account"
          columns={matrix.columns}
          columnPrefs={columnPrefs}
          totalGroup="Total"
          rows={matrix.rows}
          footer={matrix.footer}
          defaultExpanded="roots"
          onDownloadCsv={handleDownloadCsv}
          maxHeight={520}
          emptyText="No P&L rows for this period."
          tools={(
            <button
              type="button"
              className="rn-icon-btn"
              title="Minimize P&L"
              onClick={() => setCollapsed(true)}
            >
              <ChevronDown />
            </button>
          )}
          legend={[
            { label: 'Favorable variance', swatch: 'var(--color-success-bg)', border: 'var(--color-success)' },
            { label: 'Unfavorable variance', swatch: 'var(--color-error-bg)', border: 'var(--color-error)' },
            { label: 'Period total', swatch: 'var(--lodge-orange-50)', border: 'var(--lodge-orange-200)' },
            'Budget column shown muted',
          ]}
        />
      </div>
      <GlDrillModal drill={glDrill} onClose={() => setGlDrill(null)} currency={currency} />
    </>
  );
};

export default PnLReportTable;
